import React, { Fragment, useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import type { IClientUser } from '../../interfaces/user';
import Navigation from '../../components/Navigation';
import { GET_ALL_COFFEE_MATCHES } from '../../graphql/GET_ALL_COFFEE_MATCHES';
import { DELETE_COFFEE_MATCH } from '../../graphql/DELETE_COFFEE_MATCH';
import searchIcon from '../../assets/svg/search.svg';

const AdminCoffeeChatPage: React.FC<IClientUser> = (props) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [dateFilter, setDateFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortBy, setSortBy] = useState('newest');
    const matchesPerPage = 10;

    const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
    const [loadingMatchId, setLoadingMatchId] = useState<string | null>(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const { data, loading, error, refetch } = useQuery(GET_ALL_COFFEE_MATCHES);
    const matches = data?.getAllCoffeeMatches || [];

    const [deleteCoffeeMatch] = useMutation(DELETE_COFFEE_MATCH, {
        onCompleted: () => {
            refetch();
            setLoadingMatchId(null);
            setOpenDropdownId(null);
            setDeleteConfirmId(null);
        },
        onError: (error) => {
            console.error('Error deleting coffee match:', error);
            alert(`Error: ${error.message}`);
            setLoadingMatchId(null);
        }
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, dateFilter, statusFilter, sortBy]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpenDropdownId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const filterByDate = (matchList: any[]) => {
        if (dateFilter === 'all') return matchList;

        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        return matchList.filter((match: any) => {
            if (!match.matchedAt) return false;
            const matchDate = new Date(match.matchedAt);

            switch (dateFilter) {
                case 'today':
                    return matchDate >= today;
                case 'last7days': {
                    const sevenDaysAgo = new Date(today);
                    sevenDaysAgo.setDate(today.getDate() - 7);
                    return matchDate >= sevenDaysAgo;
                }
                case 'last30days': {
                    const thirtyDaysAgo = new Date(today);
                    thirtyDaysAgo.setDate(today.getDate() - 30);
                    return matchDate >= thirtyDaysAgo;
                }
                default:
                    return true;
            }
        });
    };

    const filterByStatus = (matchList: any[]) => {
        if (statusFilter === 'all') return matchList;
        return matchList.filter((match: any) => match.status === statusFilter);
    };

    const sortMatches = (matchList: any[]) => {
        const sorted = [...matchList];

        switch (sortBy) {
            case 'newest':
                return sorted.sort((a, b) => {
                    const dateA = new Date(a.matchedAt).getTime();
                    const dateB = new Date(b.matchedAt).getTime();
                    return dateB - dateA;
                });
            case 'oldest':
                return sorted.sort((a, b) => {
                    const dateA = new Date(a.matchedAt).getTime();
                    const dateB = new Date(b.matchedAt).getTime();
                    return dateA - dateB;
                });
            case 'score-high':
                return sorted.sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));
            case 'score-low':
                return sorted.sort((a, b) => (a.matchScore || 0) - (b.matchScore || 0));
            default:
                return sorted;
        }
    };

    let filteredMatches = matches.filter((match: any) => {
        const searchLower = searchTerm.toLowerCase();
        const personAName = match.personA?.profile?.name?.toLowerCase() || '';
        const personAEmail = match.personA?.email?.toLowerCase() || '';
        const personBName = match.personB?.profile?.name?.toLowerCase() || '';
        const personBEmail = match.personB?.email?.toLowerCase() || '';
        return personAName.includes(searchLower) || personAEmail.includes(searchLower) ||
               personBName.includes(searchLower) || personBEmail.includes(searchLower);
    });

    filteredMatches = filterByDate(filteredMatches);
    filteredMatches = filterByStatus(filteredMatches);
    filteredMatches = sortMatches(filteredMatches);

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getInitials = (name: string) => {
        if (!name) return '?';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const getStatusBadgeClass = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'accepted':
                return 'status-badge accepted';
            case 'pending':
                return 'status-badge pending';
            case 'declined':
                return 'status-badge declined';
            case 'expired':
                return 'status-badge expired';
            default:
                return 'status-badge';
        }
    };

    const totalPages = Math.ceil(filteredMatches.length / matchesPerPage);
    const startIndex = (currentPage - 1) * matchesPerPage;
    const endIndex = startIndex + matchesPerPage;
    const currentMatches = filteredMatches.slice(startIndex, endIndex);

    const goToPage = (page: number) => {
        setCurrentPage(page);
    };

    const prevPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const nextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const getPageNumbers = () => {
        const pages: number[] = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);

            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);

            if (currentPage <= 3) {
                endPage = 4;
            }

            if (currentPage >= totalPages - 2) {
                startPage = totalPages - 3;
            }

            if (startPage > 2) {
                pages.push(-1);
            }

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }

            if (endPage < totalPages - 1) {
                pages.push(-2);
            }

            pages.push(totalPages);
        }

        return pages;
    };

    const handleDeleteMatch = async (matchId: string) => {
        setLoadingMatchId(matchId);
        try {
            await deleteCoffeeMatch({
                variables: {
                    matchId: parseInt(matchId)
                }
            });
        } catch (error) {
        }
    };

    const toggleDropdown = (matchId: string) => {
        setOpenDropdownId(openDropdownId === matchId ? null : matchId);
    };

    const showDeleteConfirm = (matchId: string) => {
        setDeleteConfirmId(matchId);
    };

    const cancelDelete = () => {
        setDeleteConfirmId(null);
    };

    return (
        <main className="page-admin-users">
            <Fragment>
                <Navigation {...props} />
                <section className="container">
                    <div className="admin-users-container">
                        <div className="admin-header">
                            <div className="admin-header-content">
                                <h1 className="admin-title">Coffee Chat Matches</h1>
                                <p className="admin-subtitle">View and manage all coffee chat matches</p>
                            </div>
                        </div>

                        {!loading && !error && (
                            <div className="admin-stats-card">
                                <div className="stat-card">
                                    <div className="stat-icon-wrapper">
                                        <i className="fas fa-coffee"></i>
                                    </div>
                                    <div className="stat-content">
                                        <div className="stat-label">Total Matches</div>
                                        <div className="stat-value">{matches.length}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!loading && !error && (
                            <div className="admin-filters">
                                <div className="filters-left">
                                    <div className="filter-group">
                                        <label className="filter-label">Match Date:</label>
                                        <div className="select">
                                            <select
                                                value={dateFilter}
                                                onChange={(e) => setDateFilter(e.target.value)}
                                            >
                                                <option value="all">All Time</option>
                                                <option value="today">Today</option>
                                                <option value="last7days">Last 7 Days</option>
                                                <option value="last30days">Last 30 Days</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="filter-group">
                                        <label className="filter-label">Status:</label>
                                        <div className="select">
                                            <select
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                            >
                                                <option value="all">All Status</option>
                                                <option value="pending">Pending</option>
                                                <option value="accepted">Accepted</option>
                                                <option value="declined">Declined</option>
                                                <option value="expired">Expired</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="filter-group">
                                        <label className="filter-label">Sort By:</label>
                                        <div className="select">
                                            <select
                                                value={sortBy}
                                                onChange={(e) => setSortBy(e.target.value)}
                                            >
                                                <option value="newest">Newest First</option>
                                                <option value="oldest">Oldest First</option>
                                                <option value="score-high">Highest Score</option>
                                                <option value="score-low">Lowest Score</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {(dateFilter !== 'all' || statusFilter !== 'all' || sortBy !== 'newest') && (
                                    <button
                                        className="button clear-filters-btn"
                                        onClick={() => {
                                            setDateFilter('all');
                                            setStatusFilter('all');
                                            setSortBy('newest');
                                        }}
                                    >
                                        <span>Clear Filters</span>
                                    </button>
                                )}
                            </div>
                        )}

                        <div className="admin-search-container">
                            <div className="control has-icons-left">
                                <input
                                    className="input admin-search-input"
                                    type="text"
                                    placeholder="Search by person name or email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <span className="icon is-left search-icon">
                                    <img src={searchIcon} alt="Search" />
                                </span>
                            </div>
                        </div>

                        {loading && (
                            <div className="admin-loading">
                                <div className="loading-spinner"></div>
                                <p>Loading matches...</p>
                            </div>
                        )}

                        {error && (
                            <div className="admin-error">
                                <i className="fas fa-exclamation-circle"></i>
                                <p>Error loading matches: {error.message}</p>
                            </div>
                        )}

                        {!loading && !error && (
                            <>
                                {filteredMatches.length > 0 ? (
                                    <>
                                        {searchTerm && (
                                            <div className="search-results-info">
                                                <i className="fas fa-filter"></i>
                                                Showing {filteredMatches.length} of {matches.length} matches matching "{searchTerm}"
                                            </div>
                                        )}

                                        <div className="table-container">
                                            <table className="table is-fullwidth is-hoverable">
                                                <thead>
                                                    <tr>
                                                        <th className="table-number-col">#</th>
                                                        <th>Person A</th>
                                                        <th>Person B</th>
                                                        <th>Score</th>
                                                        <th>Status</th>
                                                        <th>Reason</th>
                                                        <th>Matched Date</th>
                                                        <th>Response Date</th>
                                                        <th>Email Sent</th>
                                                        <th className="table-actions-col">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {currentMatches.map((match, index) => (
                                                        <tr key={match.id}>
                                                            <td className="table-number-col">{startIndex + index + 1}</td>
                                                            <td className="table-name-col">
                                                                <div className="user-name-cell">
                                                                    <div className="user-avatar-small">
                                                                        {match.personA?.avatar?.fileUrl ? (
                                                                            <img src={match.personA.avatar.fileUrl} alt={match.personA?.profile?.name} />
                                                                        ) : (
                                                                            <span>{getInitials(match.personA?.profile?.name || match.personA?.email)}</span>
                                                                        )}
                                                                    </div>
                                                                    <div className="user-info-wrapper">
                                                                        <div className="user-name-row">
                                                                            <span>{match.personA?.profile?.name || 'Unnamed User'}</span>
                                                                        </div>
                                                                        <div className="user-email-small">{match.personA?.email}</div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="table-name-col">
                                                                <div className="user-name-cell">
                                                                    <div className="user-avatar-small">
                                                                        {match.personB?.avatar?.fileUrl ? (
                                                                            <img src={match.personB.avatar.fileUrl} alt={match.personB?.profile?.name} />
                                                                        ) : (
                                                                            <span>{getInitials(match.personB?.profile?.name || match.personB?.email)}</span>
                                                                        )}
                                                                    </div>
                                                                    <div className="user-info-wrapper">
                                                                        <div className="user-name-row">
                                                                            <span>{match.personB?.profile?.name || 'Unnamed User'}</span>
                                                                        </div>
                                                                        <div className="user-email-small">{match.personB?.email}</div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span className="match-score">{match.matchScore ? Math.round(match.matchScore) : 'N/A'}</span>
                                                            </td>
                                                            <td>
                                                                <span className={getStatusBadgeClass(match.status)}>
                                                                    {match.status}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className="match-reason">{match.reason || 'N/A'}</span>
                                                            </td>
                                                            <td>{formatDate(match.matchedAt)}</td>
                                                            <td>{formatDate(match.respondedAt)}</td>
                                                            <td>
                                                                {match.emailSent ? (
                                                                    <span className="email-status sent">
                                                                        <i className="fas fa-check-circle"></i> Sent
                                                                    </span>
                                                                ) : (
                                                                    <span className="email-status not-sent">
                                                                        <i className="fas fa-times-circle"></i> Not Sent
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="table-actions-col">
                                                                <div className="actions-dropdown-wrapper" ref={openDropdownId === match.id ? dropdownRef : null}>
                                                                    <button
                                                                        className="action-button delete-btn"
                                                                        onClick={() => toggleDropdown(match.id)}
                                                                        disabled={loadingMatchId === match.id}
                                                                    >
                                                                        {loadingMatchId === match.id ? (
                                                                            <span className="dropdown-loading-spinner"></span>
                                                                        ) : (
                                                                            <>
                                                                                <i className="fas fa-trash"></i>
                                                                                <span>Delete</span>
                                                                            </>
                                                                        )}
                                                                    </button>

                                                                    {openDropdownId === match.id && (
                                                                        <div className="status-dropdown-menu">
                                                                            <button
                                                                                className="status-option delete"
                                                                                onClick={() => showDeleteConfirm(match.id)}
                                                                            >
                                                                                <i className="fas fa-exclamation-triangle"></i>
                                                                                <span>Confirm Delete</span>
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className="pagination-wrapper">
                                            <div className="pagination-info">
                                                Showing {startIndex + 1}-{Math.min(endIndex, filteredMatches.length)} of {filteredMatches.length} matches
                                            </div>

                                            {totalPages > 1 && (
                                                <div className="pagination-controls">
                                                    <button
                                                        className="pagination-btn"
                                                        onClick={prevPage}
                                                        disabled={currentPage === 1}
                                                    >
                                                        <i className="fas fa-chevron-left"></i>
                                                        Previous
                                                    </button>

                                                    <div className="pagination-numbers">
                                                        {getPageNumbers().map((page, index) => {
                                                            if (page < 0) {
                                                                return (
                                                                    <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                                                                        ...
                                                                    </span>
                                                                );
                                                            }
                                                            return (
                                                                <button
                                                                    key={page}
                                                                    className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                                                                    onClick={() => goToPage(page)}
                                                                >
                                                                    {page}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>

                                                    <button
                                                        className="pagination-btn"
                                                        onClick={nextPage}
                                                        disabled={currentPage === totalPages}
                                                    >
                                                        Next
                                                        <i className="fas fa-chevron-right"></i>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div className="admin-empty">
                                        <i className="fas fa-coffee"></i>
                                        <p className="empty-title">No matches found</p>
                                        <p className="empty-subtitle">
                                            {searchTerm
                                                ? `No matches match "${searchTerm}"`
                                                : 'No coffee chat matches yet'}
                                        </p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </section>

                {deleteConfirmId && (
                    <div className="modal is-active">
                        <div className="modal-background" onClick={cancelDelete}></div>
                        <div className="modal-card">
                            <header className="modal-card-head">
                                <p className="modal-card-title">Confirm Delete</p>
                                <button className="delete" aria-label="close" onClick={cancelDelete}></button>
                            </header>
                            <section className="modal-card-body">
                                <p>Are you sure you want to delete this coffee chat match? This action cannot be undone.</p>
                            </section>
                            <footer className="modal-card-foot">
                                <button className="button is-danger" onClick={async () => await handleDeleteMatch(deleteConfirmId)}>
                                    Delete Match
                                </button>
                                <button className="button" onClick={cancelDelete}>Cancel</button>
                            </footer>
                        </div>
                    </div>
                )}
            </Fragment>
        </main>
    );
};

export default AdminCoffeeChatPage;